import React, { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { FaCalendarAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import U2000CalendarPopup, { type CalendarLabels } from "./U2000CalendarPopup";
import { parseDateWithFormat, validateDate } from "../../../tools/U2000DateUtils";

interface U2000CalendarValue {
  value?: string;
  timestamp?: number;
}

interface U2000CalendarProps {
  placeHolder?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  value?: U2000CalendarValue;
  onValueChange?: (value: string) => void;
  dateFormat?: string;
  /** Pied du popup : Aujourd'hui | saisie | Effacer (réservé à la recherche via `U2000SearchCriteriaDateField`). */
  showPopupFooterDateInput?: boolean;
}

const U2000Calendar: React.FC<U2000CalendarProps> = ({
  placeHolder,
  onFocus,
  onBlur,
  disabled = false,
  value,
  onValueChange,
  dateFormat = "yyyy-MM-dd",
  showPopupFooterDateInput = false,
}) => {
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [isOpenCalendar, setIsOpenCalendar] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");

  const parentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Préparer les labels pour le calendrier popup
  const calendarLabels: CalendarLabels = {
    monthNames: [
      t("lblCalendarJan"),
      t("lblCalendarFeb"),
      t("lblCalendarMar"),
      t("lblCalendarApr"),
      t("lblCalendarMay"),
      t("lblCalendarJun"),
      t("lblCalendarJul"),
      t("lblCalendarAug"),
      t("lblCalendarSep"),
      t("lblCalendarOct"),
      t("lblCalendarNov"),
      t("lblCalendarDec"),
    ],
    dayNames: [t("lblCalendarSun"), t("lblCalendarMon"), t("lblCalendarTue"), t("lblCalendarWed"), t("lblCalendarThu"), t("lblCalendarFri"), t("lblCalendarSat")],
    today: t("lblCalendarToday"),
    clear: t("lblCalendarClear"),
  };

  function handleCalendarClick(): void {
    if (disabled) return;
    if (!isOpenCalendar && onFocus) onFocus();
    if (isOpenCalendar && onBlur) onBlur();
    if (!isOpenCalendar) {
      setIsFocused(true);
      if (showPopupFooterDateInput) {
        if (value?.value) {
          const date = parseDateWithFormat(value.value, "yyyy-MM-dd") || new Date(`${value.value}T00:00:00`);
          setInputValue(format(date, dateFormat));
          setCurrentMonth(date);
        } else {
          setInputValue("");
          setHasError(false);
        }
      }
    }
    setIsOpenCalendar(!isOpenCalendar);
  }

  const validateDateStr = (dateStr: string): boolean => {
    return validateDate(dateStr, dateFormat);
  };

  function applyManualDateInput(val: string): void {
    const v = val.replace(/[^0-9/\-]/g, "");
    if (v === "") {
      setHasError(false);
      setInputValue("");
      if (onValueChange) onValueChange("");
      return;
    }
    if (validateDateStr(v)) {
      setHasError(false);
      const date = parseDateWithFormat(v, dateFormat);
      if (date) {
        setCurrentMonth(date);
        setInputValue(v);
        if (onValueChange) onValueChange(format(date, "yyyy-MM-dd"));
      }
    } else {
      setHasError(true);
      setInputValue(v);
    }
  }

  /** Saisie en cours dans le pied du popup : autoriser les valeurs partielles sans pousser le parent. */
  function handlePopupFooterInputChange(val: string): void {
    const v = val.replace(/[^0-9/\-]/g, "");
    setInputValue(v);
    setHasError(v !== "" && !validateDateStr(v));
  }

  function hangleInputChangeDate(val: string): void {
    applyManualDateInput(val);
  }

  function commitPopupFooterDateInput(): void {
    applyManualDateInput(inputValue);
  }

  function handleSelectData(selectedDate: Date): void {
    const ymd = format(selectedDate, "yyyy-MM-dd");
    setInputValue(format(selectedDate, dateFormat));
    setCurrentMonth(selectedDate);
    setHasError(false);
    if (onValueChange) onValueChange(ymd);
    if (!showPopupFooterDateInput) {
      setIsOpenCalendar(false);
    }
  }

  function handleTodayClick(): void {
    const now = new Date();
    setCurrentMonth(now);
    setCurrentDate(now);
    const formatted = format(now, dateFormat);
    setInputValue(formatted);
    setHasError(false);
    if (onValueChange) onValueChange(format(now, "yyyy-MM-dd"));
    if (!showPopupFooterDateInput) {
      setIsOpenCalendar(false);
    }
  }

  function handleClearClick(): void {
    setInputValue("");
    setHasError(false);
    if (onValueChange) onValueChange("");
    if (!showPopupFooterDateInput) {
      setIsOpenCalendar(false);
    }
  }

  function onInputFocus(): void {
    setIsOpenCalendar(false);
    setIsFocused(true);
    if (onFocus) onFocus();
  }

  function onInputBlur(): void {
    setIsFocused(false);
    if (onBlur) onBlur();
  }

  // Gestion du clic à l'extérieur pour fermer le calendrier
  useEffect(() => {
    if (!isOpenCalendar) return;

    function handleClickOutside(event: MouseEvent) {
      if (parentRef.current && !parentRef.current.contains(event.target as Node)) {
        setIsOpenCalendar(false);
        setIsFocused(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpenCalendar]);

  useEffect(() => {
    if (isOpenCalendar && showPopupFooterDateInput) {
      return;
    }
    if (value && value.value) {
      const date = parseDateWithFormat(value.value, "yyyy-MM-dd") || new Date(value.value + "T00:00:00");
      setInputValue(format(date, dateFormat));
    } else {
      setInputValue("");
    }
  }, [value?.timestamp, dateFormat, isOpenCalendar, showPopupFooterDateInput]);

  // Classes modernes pour le calendrier
  const getCalendarClasses = () => {
    const baseClasses = "flex items-center w-full px-4 py-3 text-sm bg-white border rounded-lg transition-all duration-200 cursor-pointer focus-within:outline-hidden focus-within:ring-2";

    if (disabled) {
      return `${baseClasses} bg-gray-50 text-gray-500 border-gray-200 cursor-not-allowed`;
    }

    if (hasError) {
      return `${baseClasses} border-red-300 focus-within:border-red-500 focus-within:ring-red-500/20 text-gray-900`;
    }

    if (isFocused || isOpenCalendar) {
      return `${baseClasses} border-blue-500 ring-blue-500/20 text-gray-900`;
    }

    return `${baseClasses} border-gray-300 hover:border-gray-400 text-gray-900`;
  };

  return (
    <div className="relative w-full" ref={parentRef}>
      <div
        className={getCalendarClasses()}
        style={{ height: "46px" }}
        onClick={handleCalendarClick}
        role="combobox"
        aria-expanded={isOpenCalendar}
        aria-haspopup="dialog"
        aria-label="Sélectionner une date"
      >
        <input
          type="text"
          value={inputValue}
          placeholder={placeHolder || dateFormat}
          className="flex-1 bg-transparent text-sm outline-hidden placeholder:text-gray-400 disabled:cursor-not-allowed"
          onChange={(e) => hangleInputChangeDate(e.target.value)}
          disabled={disabled}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              setIsOpenCalendar(false);
            } else if (e.key === "F8" || e.key === "Enter") {
              e.preventDefault();
              if (disabled) return;
              setIsOpenCalendar(true);
            }
          }}
          aria-label={`Date au format ${dateFormat}`}
        />

        <button
          type="button"
          className={`flex items-center justify-center w-6 h-6 transition-colors duration-200 rounded
                     ${disabled ? "text-gray-400 cursor-not-allowed" : "text-gray-500 hover:text-blue-600 focus:outline-hidden focus:text-blue-600"}`}
          disabled={disabled}
          aria-label="Ouvrir le calendrier"
          tabIndex={-1}
        >
          <FaCalendarAlt className="h-4 w-4" />
        </button>
      </div>

      <U2000CalendarPopup
        isOpen={isOpenCalendar}
        currentDate={currentDate}
        currentMonth={currentMonth}
        mode="single"
        position="bottom"
        selectedValue={value?.value}
        labels={calendarLabels}
        onSelectDate={handleSelectData}
        onTodayClick={handleTodayClick}
        onClearClick={handleClearClick}
        onMonthChange={setCurrentMonth}
        onClose={() => setIsOpenCalendar(false)}
        footerDateInput={
          showPopupFooterDateInput
            ? {
                value: inputValue,
                placeholder: dateFormat,
                disabled,
                onChange: handlePopupFooterInputChange,
                onCommit: commitPopupFooterDateInput,
              }
            : undefined
        }
        zIndex={1000}
      />
    </div>
  );
};

export default U2000Calendar;
